/* eslint-disable jsx-a11y/label-has-associated-control */

import React, { LegacyRef, useRef, useState } from 'react';
import {
  HomefieldIconSocialFacebook,
  HomefieldIconSocialInstagram,
  HomefieldIconSocialPinterest,
  HomefieldIconSocialX,
  HomefieldIconSocialYoutube,
} from '@dsg-tech/homefield-react-icons';
import { handleADA } from '@dsg-tech/homefield-utils';
import { Button, TextLink } from '@dsg-tech/homefield-react';

import { FooterTaxonomy } from 'types/index';
import { getFeatureFlagByNameSelector } from 'store/selectors';
import { useAppSelector } from 'store/store';
import useClientSideRender from 'hooks/useClientSideRender';
import useMobileSignUp from 'hooks/useMobileSignUp';
import useLogger from 'hooks/useLogger';
import PhoneInput from '../../../common/PhoneInput/PhoneInput';
import './ConnectWithUs.scss';

type Props = {
  storeIdentifier: string;
  chatUrl: string;
  emailOnSignUp: () => void;
  communications: FooterTaxonomy['Communication Section'];
  social: {
    youtube: string;
    twitter: string;
    facebook: string;
    pinterest: string;
    instagram: string;
  };
  emailFocusState: boolean;
  emailErrorStatus: boolean;
  emailSignupInput: LegacyRef<HTMLInputElement>;
  emailErrorMessage: string;
  emailSuccessStatus: boolean;
  emailSuccessMessage: string;
  emailOnBlur: () => void;
  emailOnFocus: () => void;
};

const ConnectWithUs = ({
  storeIdentifier,
  chatUrl,
  emailOnSignUp,
  communications,
  social,
  emailFocusState,
  emailErrorStatus,
  emailSignupInput,
  emailErrorMessage,
  emailSuccessStatus,
  emailSuccessMessage,
  emailOnBlur,
  emailOnFocus,
}: Props) => {
  const { isClient } = useClientSideRender();
  const {
    makeSmsSignUpCall,
    mobileSignUpErrorMessage,
    isMobileSignUpSuccess,
    validatePhoneNumber,
  } = useMobileSignUp();

  const smsSignUpEnabled = useAppSelector(getFeatureFlagByNameSelector('smsSignUpEnabled'));
  const [mobile, setMobile] = useState<string>('');
  const [isMobileFocused, setIsMobileFocused] = useState<boolean>(false);
  const mobileSignUpInputRef = useRef<HTMLInputElement>(null);
  const { error } = useLogger();
  const handleOpenChat = (event: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    if (typeof window === 'undefined') {
      return;
    }
    const evt = new CustomEvent('ChatClicked', {
      detail: { Location: 'Footer_ChatNow' },
    });

    window.dispatchEvent(evt);
    window.location.href = chatUrl;
  };

  const handleKeyPressSignUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    handleADA(event, 'input', emailOnSignUp);
  };

  const onMobileSignUp = async () => {
    await makeSmsSignUpCall(mobile);
  };

  const onMobileFocus = () => {
    setIsMobileFocused(!isMobileFocused);
  };

  const onMobileBlur = () => {
    onMobileFocus();
    if (mobileSignUpInputRef.current && mobileSignUpInputRef.current !== null) {
      const mobileNumber = mobileSignUpInputRef.current.value;
      if (mobileNumber) {
        validatePhoneNumber(mobileNumber);
      }
    }
  };

  const onMobileKeyPressSignUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    handleADA(event, 'input', onMobileSignUp);
  };
  const legalDisclaimer = () => {
    switch (storeIdentifier) {
      case 'dsg':
        return 'Valid in-store and online. First-time subscribers only. Returning subscribers will be resubscribed. Expires 7 days after signing up. Exclusions and terms apply.';
      case 'g3':
        return 'In-store and online. First-time subscribers only. Returning subscribers will be resubscribed for marketing/promo emails.';
      default:
        return 'Online only. First-time subscribers only. Returning subscribers will be resubscribed for marketing/promo emails.';
    }
  };

  try {
    const communication = communications?.[0];
    const communicationMessage = communication?.['Communication Message']?.[0];
    const socials = social;

    const emdata = communicationMessage?.smsHeadline
      ? `Footer_${communicationMessage.smsHeadline.replace(/[^A-Z0-9]+/gi, '')}`
      : null;

    return (
      <div className="footer-connect-with-us hmf-display-flex hmf-flex-wrap hmf-px-s hmf-p-sm-m hmf-px-md-m hmf-py-xlg-xl hmf-py-md-l hmf-py-m">
        <div className="hmf-display-flex hmf-flex-column hmf-flex-grow hmf-text-align-center hmf-text-align-m-left">
          <h3 className="hmf-display-s hmf-m-0">{communication?.headline}</h3>

          <div className="hmf-display-flex hmf-flex-column hmf-flex-md-row hmf-pb-m">
            <div className="email-sign-up hmf-display-flex hmf-flex-column hmf-mt-s hmf-pr-m-s">
              <strong className="hmf-body-bold-l hmf-pb-xs">
                {communicationMessage?.emailHeadline}
              </strong>

              <div className="hmf-display-flex hmf-pb-xxxs">
                {!emailSuccessStatus ? (
                  <>
                    <label
                      htmlFor="email-signup"
                      className={`email-signup-label hmf-position-absolute hmf-body-s ${
                        emailFocusState && 'dsg-react-input-focused'
                      } ${emailErrorStatus && 'dsg-react-input-error'}`}>
                      Email Address
                    </label>
                    {isClient && (
                      <input
                        id="email-signup"
                        type="email"
                        placeholder="Email Address"
                        className={`sign-up-input hmf-body-s hmf-px-s hmf-py-xs hmf-flex-grow ${
                          emailErrorStatus && 'dsg-react-input-error'
                        }`}
                        onBlur={emailOnBlur}
                        onFocus={emailOnFocus}
                        onKeyPress={handleKeyPressSignUp}
                        ref={emailSignupInput}
                      />
                    )}
                    <Button
                      className="connect-with-us-button hmf-px-m hmf-flex-grow hmf-sharp-radius"
                      variant="primary"
                      aria-label="Sign Up"
                      label="Sign Up"
                      text="Sign Up"
                      onClick={emailOnSignUp}
                    />
                  </>
                ) : (
                  <>{emailSuccessMessage}</>
                )}
              </div>
              {emailErrorStatus && (
                <div className="dsg-react-footer-sign-up-error-msg hmf-legal-xs hmf-pl-xs hmf-pt-xxxs hmf-pb-xxs">
                  {emailErrorMessage}
                </div>
              )}

              <span className="hmf-body-s">
                <sup>*</sup>
                {legalDisclaimer()}
              </span>
            </div>
            {smsSignUpEnabled ? (
              <div className="hmf-display-flex hmf-flex-column hmf-mt-s">
                {isMobileSignUpSuccess ? (
                  <>
                    <strong className="success-sms hmf-header-bold-s hmf-text-align-left">
                      Success! Check your Texts.
                    </strong>
                    <p className="hmf-legal-xs hmf-mt-xxs hmf-text-align-left">
                      Reply Y to confirm your subscription.
                    </p>
                  </>
                ) : (
                  <>
                    <strong className="hmf-header-bold-s hmf-pb-xs">
                      {communicationMessage?.smsHeadline}
                      <sup>**</sup>
                    </strong>
                    <div className="dsg-react-footer-sms-sign-up-form-container hmf-display-flex hmf-pb-xxxs">
                      <label
                        htmlFor="sms-signup"
                        className={`dsg-react-footer-sign-up-form-label hmf-position-absolute hmf-body-s ${
                          isMobileFocused && 'dsg-react-input-focused'
                        } ${mobileSignUpErrorMessage && 'dsg-react-input-error'}`}>
                        Mobile Number
                      </label>
                      <PhoneInput
                        id="sms-signup"
                        type="tel"
                        placeholder="Mobile Number"
                        className={`hmf-body-s hmf-px-s hmf-py-xs ${
                          mobileSignUpErrorMessage && 'dsg-react-input-error'
                        }`}
                        onBlur={onMobileBlur}
                        setValue={setMobile}
                        value={mobile}
                        onFocus={onMobileFocus}
                        onKeyPress={onMobileKeyPressSignUp}
                        validatePhoneNumber={validatePhoneNumber}
                        mobileSignUpErrorMessage={mobileSignUpErrorMessage}
                        mobileSignUpInputRef={mobileSignUpInputRef}
                      />
                      <Button
                        className="connect-with-us-button hmf-px-m hmf-flex-grow hmf-sharp-radius"
                        variant="primary"
                        aria-label="Sign Up"
                        label="Sign Up"
                        text="Sign Up"
                        onClick={onMobileSignUp}
                      />
                    </div>
                    {!!mobileSignUpErrorMessage && (
                      <div className="dsg-react-footer-sign-up-error-msg hmf-legal-xs hmf-pl-xs hmf-pt-xxxs hmf-pb-xxs">
                        {mobileSignUpErrorMessage}
                      </div>
                    )}
                    <span className="hmf-body-s">{communicationMessage?.smsBody}</span>
                  </>
                )}
              </div>
            ) : (
              communicationMessage?.smsHeadline &&
              communicationMessage?.smsBody && (
                <div className="mobile-alert-sign-up hmf-display-flex hmf-flex-column hmf-px-s hmf-mt-s">
                  <strong className="hmf-body-bold-l hmf-pb-xs hmf-mt-xxxs">
                    {communicationMessage.smsHeadline}
                  </strong>
                  <span className="hmf-body-s">
                    {communicationMessage.smsBody}{' '}
                    {communicationMessage.smsBody && (
                      <TextLink
                        variant="tertiary"
                        size="small"
                        href={communicationMessage?.sms_link}
                        data-em={emdata}>
                        {communicationMessage.sms_link_text}
                      </TextLink>
                    )}
                  </span>
                </div>
              )
            )}
          </div>
        </div>
        <div className="need-help-container hmf-display-flex hmf-flex-column hmf-pt-m">
          <div className="hmf-display-flex hmf-flex-wrap hmf-align-items-center hmf-align-items-m-flex-start hmf-justify-content-center hmf-flex-column">
            <div className="hmf-header-bold-s hmf-mb-xxs">Need More Help?</div>
            <Button
              className="connect-with-us-button hmf-px-m hmf-flex-grow"
              variant="secondary"
              aria-label="Contact Us"
              label="Contact Us"
              text="Contact Us"
              onClick={handleOpenChat}
            />
          </div>

          <div className="social-media-links hmf-display-flex hmf-justify-content-center hmf-mt-m">
            {socials.youtube && (
              <a
                href={socials.youtube}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="follow us on youtube">
                <HomefieldIconSocialYoutube height="24px" width="24px" />
              </a>
            )}
            {socials.twitter && (
              <a
                href={socials.twitter}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="follow us on X">
                <HomefieldIconSocialX height="24px" width="24px" />
              </a>
            )}
            {socials.facebook && (
              <a
                href={socials.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="follow us on facebook">
                <HomefieldIconSocialFacebook height="24px" width="24px" />
              </a>
            )}
            {socials.pinterest && (
              <a
                href={socials.pinterest}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="follow us on pinterest">
                <HomefieldIconSocialPinterest height="24px" width="24px" />
              </a>
            )}
            {socials.instagram && (
              <a
                href={socials.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="follow us on instagram">
                <HomefieldIconSocialInstagram height="24px" width="24px" />
              </a>
            )}
          </div>
        </div>
      </div>
    );
  } catch (e) {
    error('Could not load ConnectWithUs - ', e);
    return null;
  }
};

export default ConnectWithUs;
